import * as THREE from 'three';
import Application from '../Application';
import EventEmitter from '../Utils/EventEmitter';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import TWEEN from '@tweenjs/tween.js';
import UIEventBus from '../UI/EventBus';
import { getSiteUrl } from '../UI/mobile';
import BezierEasing from 'bezier-easing';
import { MonitorKeyframe, IdleKeyframe, LoadingKeyframe, DeskKeyframe, OrbitControlsStart, } from './CameraKeyframes';
// Mouvement de zoom vers le moniteur. Partage par le survol de l'ecran au
// bureau (setMonitorListeners) et par la sortie mobile vers le site marketing
// (setMobileExitListener) : c'est litteralement le meme mouvement, pas deux
// reglages a tenir alignes.
const MONITOR_ZOOM_DURATION = 2000;
const MONITOR_ZOOM_EASING = BezierEasing(0.13, 0.99, 0, 1);
// Temps d'arret sur l'ecran cadre avant de quitter la page, sur mobile. Sans
// lui la navigation part sur la derniere image du zoom et l'ecran qu'on vient
// juste de cadrer n'est jamais vu.
const MOBILE_SITE_HOLD = 450;
export var CameraKey;
(function (CameraKey) {
    CameraKey["IDLE"] = "idle";
    CameraKey["MONITOR"] = "monitor";
    CameraKey["LOADING"] = "loading";
    CameraKey["DESK"] = "desk";
    CameraKey["ORBIT_CONTROLS_START"] = "orbitControlsStart";
})(CameraKey || (CameraKey = {}));
export default class Camera extends EventEmitter {
    constructor() {
        super();
        this.application = new Application();
        this.sizes = this.application.sizes;
        this.scene = this.application.scene;
        this.renderer = this.application.renderer;
        this.resources = this.application.resources;
        this.time = this.application.time;
        this.position = new THREE.Vector3(0, 0, 0);
        this.focalPoint = new THREE.Vector3(0, 0, 0);
        this.freeCam = false;
        this.mobileExiting = false;
        this.keyframes = {
            idle: new IdleKeyframe(),
            monitor: new MonitorKeyframe(),
            loading: new LoadingKeyframe(),
            desk: new DeskKeyframe(),
            orbitControlsStart: new OrbitControlsStart(),
        };
        document.addEventListener('mousedown', (event) => {
            event.preventDefault();
            // @ts-ignore
            if (event.target.id === 'prevent-click')
                return;
            // print target and current keyframe
            if (this.currentKeyframe === CameraKey.IDLE ||
                this.targetKeyframe === CameraKey.IDLE) {
                this.transition(CameraKey.DESK);
            }
            else if (this.currentKeyframe === CameraKey.DESK ||
                this.targetKeyframe === CameraKey.DESK) {
                this.transition(CameraKey.IDLE);
            }
        });
        this.setPostLoadTransition();
        this.setInstance();
        this.setMonitorListeners();
        this.setFreeCamListeners();
        this.setMobileExitListener();
    }
    transition(key, duration = 1000, easing, callback) {
        // Sortie mobile en cours : le cadrage reste verrouille sur le
        // moniteur. Sans ce verrou, le bouton camera libre ou un leftMonitor
        // declenche par un mousemove synthetique ramenerait la camera en
        // arriere au milieu du zoom.
        if (this.mobileExiting) {
            if (key !== CameraKey.MONITOR)
                return;
            // Deja en route : relancer la tween repartirait de la position
            // courante pour une duree pleine et saccaderait le mouvement.
            if (this.targetKeyframe === CameraKey.MONITOR)
                return;
        }
        if (this.currentKeyframe === key)
            return;
        if (this.targetKeyframe)
            TWEEN.removeAll();
        this.currentKeyframe = undefined;
        this.targetKeyframe = key;
        const keyframe = this.keyframes[key];
        const posTween = new TWEEN.Tween(this.position)
            .to(keyframe.position, duration)
            .easing(easing || TWEEN.Easing.Quintic.InOut)
            .onComplete(() => {
            this.currentKeyframe = key;
            this.targetKeyframe = undefined;
            if (callback)
                callback();
        });
        const focTween = new TWEEN.Tween(this.focalPoint)
            .to(keyframe.focalPoint, duration)
            .easing(easing || TWEEN.Easing.Quintic.InOut);
        posTween.start();
        focTween.start();
    }
    setInstance() {
        this.instance = new THREE.PerspectiveCamera(35, this.sizes.width / this.sizes.height, 10, 900000);
        this.currentKeyframe = CameraKey.LOADING;
        this.scene.add(this.instance);
    }
    setMonitorListeners() {
        this.on('enterMonitor', () => {
            this.transition(CameraKey.MONITOR, MONITOR_ZOOM_DURATION, MONITOR_ZOOM_EASING);
            UIEventBus.dispatch('enterMonitor', {});
        });
        this.on('leftMonitor', () => {
            this.transition(CameraKey.DESK);
            UIEventBus.dispatch('leftMonitor', {});
        });
    }
    setFreeCamListeners() {
        UIEventBus.on('freeCamToggle', (toggle) => {
            // if (toggle === this.freeCam) return;
            if (toggle) {
                this.transition(CameraKey.ORBIT_CONTROLS_START, 750, BezierEasing(0.13, 0.99, 0, 1), () => {
                    this.instance.position.copy(this.keyframes.orbitControlsStart.position);
                    this.orbitControls.update();
                    this.freeCam = true;
                });
                // @ts-ignore
                document.getElementById('webgl').style.pointerEvents = 'auto';
            }
            else {
                this.freeCam = false;
                this.transition(CameraKey.IDLE, 4000, TWEEN.Easing.Exponential.Out);
                // @ts-ignore
                document.getElementById('webgl').style.pointerEvents = 'none';
            }
        });
    }
    /**
     * Mobile et tablette : le geste de depart rejoue le zoom vers le moniteur
     * du bureau, puis part sur le site marketing quand l'ecran remplit le
     * cadre. Le geste redirigeait jusqu'ici immediatement, et le visiteur ne
     * voyait pas d'ou sortait le site.
     */
    setMobileExitListener() {
        UIEventBus.on('mobileEnterSite', () => {
            if (this.mobileExiting)
                return;
            const target = getSiteUrl();
            // Pas d'URL de site : on laisse le visiteur dans la scene plutot
            // que de verrouiller la camera sur un moniteur sans issue.
            if (!target)
                return;
            this.mobileExiting = true;
            this.transition(CameraKey.MONITOR, MONITOR_ZOOM_DURATION, MONITOR_ZOOM_EASING);
            // La redirection est portee par une minuterie, et non par la fin
            // de la tween : tout appel a transition() commence par
            // TWEEN.removeAll(), donc un appui sur un bouton de l'interface
            // supprimerait le onComplete et laisserait le visiteur bloque
            // face au moniteur, sans moyen de revenir.
            window.setTimeout(() => {
                window.location.assign(target);
            }, MONITOR_ZOOM_DURATION + MOBILE_SITE_HOLD);
        });
    }
    setPostLoadTransition() {
        UIEventBus.on('loadingScreenDone', () => {
            this.transition(CameraKey.IDLE, 2500, TWEEN.Easing.Exponential.Out);
        });
    }
    resize() {
        this.instance.aspect = this.sizes.width / this.sizes.height;
        this.instance.updateProjectionMatrix();
    }
    createControls() {
        this.renderer = this.application.renderer;
        this.orbitControls = new OrbitControls(this.instance, this.renderer.instance.domElement);
        const { x, y, z } = this.keyframes.orbitControlsStart.focalPoint;
        this.orbitControls.target.set(x, y, z);
        this.orbitControls.enablePan = false;
        this.orbitControls.enableDamping = true;
        this.orbitControls.object.position.copy(this.keyframes.orbitControlsStart.position);
        this.orbitControls.dampingFactor = 0.05;
        this.orbitControls.maxPolarAngle = Math.PI / 2;
        this.orbitControls.minDistance = 4000;
        this.orbitControls.maxDistance = 29000;
        this.orbitControls.update();
    }
    update() {
        TWEEN.update();
        if (this.freeCam && this.orbitControls) {
            this.position.copy(this.orbitControls.object.position);
            this.focalPoint.copy(this.orbitControls.target);
            this.orbitControls.update();
            return;
        }
        for (const key in this.keyframes) {
            const _key = key;
            this.keyframes[_key].update();
        }
        if (this.currentKeyframe) {
            const keyframe = this.keyframes[this.currentKeyframe];
            this.position.copy(keyframe.position);
            this.focalPoint.copy(keyframe.focalPoint);
        }
        this.instance.position.copy(this.position);
        this.instance.lookAt(this.focalPoint);
    }
}
